import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useDispatch, useSelector } from 'react-redux'
import Table from 'modules/Statistics/subcomponents/Layout/Table'
import PropTypes from 'prop-types'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Button, Grid, Icon } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import MultipleSelect from 'components/MultipleSelect'
import { useTableConfig } from 'modules/Statistics/Account/subcomponents/TableComponent/hooks/useTableConfig'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import { useParams } from 'react-router'
import useEnableHiding from 'modules/Statistics/hooks/useEnableHiding'
import clsx from 'clsx'

const TableComponent = ({ filters, isDemoMode, chartVisible, setChartVisible }) => {
  const { network, externalId } = useParams()
  const { table } = useSelector((state) => state['statistics/account'])
  const { mds, sm } = useBreakpoint()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [sorting, setSorting] = useState()

  const [statisticsAccountTableConfig, setStatisticsAccountTableConfig] = useLocalStorage(
    `statisticsAccountTableConfig`,
    { columnVisibility: {} },
  )

  const [columnVisibility, setColumnVisibility] = useState(
    statisticsAccountTableConfig.columnVisibility,
  )

  const tableConfig = useTableConfig({
    externalId,
    network,
    filters,
    summary: table.summary,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    className: clsx('margin-bottom-8', styles['table'], styles['sticky']),
  })

  const enableHiding = useEnableHiding(tableConfig)

  const setDefaultVisibility = useCallback(() => {
    setColumnVisibility(() =>
      enableHiding.reduce((acc, item) => {
        acc[item.value] = true
        return acc
      }, {}),
    )
  }, [setColumnVisibility, enableHiding])

  useEffect(() => {
    setStatisticsAccountTableConfig({ columnVisibility })
  }, [columnVisibility])

  useEffect(() => {
    setSorting(() => [{ id: tableConfig.columns[0].id, desc: true }])
    setDefaultVisibility()
  }, [])

  const selectedColumns = useMemo(
    () =>
      Object.keys(columnVisibility).reduce((acc, item) => {
        if (columnVisibility[item]) {
          acc.push(item)
        }
        return acc
      }, []),
    [columnVisibility],
  )

  return (
    <>
      <Grid
        gap={16}
        alignItems={'center'}
        className={styles['table_switcher-panel']}
        wrap={false}
        justifyContent={'flex-end'}
      >
        <MultipleSelect
          buttonProps={{
            size: sm ? 's' : 'm',
          }}
          name={'columns'}
          type={'multiple'}
          placeholder={t('statistics::table_switcher.choose_columns_placeholder')}
          listTitle={t('statistics::table_switcher.choose_columns')}
          value={selectedColumns}
          values={enableHiding}
          onChange={(newSelection) => {
            setColumnVisibility(
              enableHiding.reduce((acc, item) => {
                acc[item.value] = newSelection.includes(item.value)
                return acc
              }, {}),
            )
          }}
          onReset={setDefaultVisibility}
          apply={t('common::button_labels.apply')}
          dropSelection={t('common::button_labels.drop_selection')}
        />

        {chartVisible !== undefined && (
          <Button
            onClick={() => setChartVisible(!chartVisible)}
            className={styles['hide-chart-button']}
            variant={'ghost'}
            grey
            size={'m'}
            noLabel={mds}
            startIcon={
              <Icon
                name={chartVisible ? 'eye-hidden' : 'eye-visible'}
                color={'inherit'}
              />
            }
          >
            {t('statistics::filters.chart_visiblity', {
              context: chartVisible ? 'hide' : 'show',
            })}
          </Button>
        )}
      </Grid>
      <Table
        table={table}
        isDemoMode={isDemoMode}
        tableConfig={tableConfig}
        filters={filters}
        dropTableData={() =>
          dispatch({
            type: 'STATISTICS/ACCOUNT_DROP_TABLE_DATA',
          })
        }
        onTableParamsUpdate={(params) =>
          dispatch({
            type: 'STATISTICS/ACCOUNT_FETCH_TABLE',
            payload: {
              ...params,
              externalId: externalId,
              network: NETWORK_PREFIX_MAP[network],
            },
          })
        }
      />
    </>
  )
}

TableComponent.propTypes = {
  filters: PropTypes.object.isRequired,
  isDemoMode: PropTypes.bool,
  chartVisible: PropTypes.bool,
  setChartVisible: PropTypes.func,
}

export default TableComponent
